<template>
  <div class="flex flex-row items-center gap-1">
    <span
      class="squircle-lg flex shrink-0 items-center rounded border bg-white text-gray-700 select-none"
    >
      <div v-if="hidePrice" class="flex items-center gap-2 px-2">
        <span class="font-bold"> {{ displayedTva }}% </span>
      </div>
      <div v-else class="flex items-center gap-2 px-2">
        <span class="text-xs uppercase">{{ displayPriceType }}</span>
        <span class="font-bold"
          >{{ displayedPrice }} ({{ displayedTva }}%)
        </span>
      </div>
      <Popover v-if="item.advanced_pricing" v-slot="{ open }">
        <Float placement="bottom-end" portal :offset="5">
          <PopoverButton
            class="bg-secondary text-contrast-secondary h-5 self-center px-1"
          >
            <FontAwesomeIcon :icon="['fal', 'euro-sign']" size="sm" />
            <FontAwesomeIcon
              :icon="['fal', open ? 'chevron-up' : 'chevron-down']"
            />
          </PopoverButton>
          <PopoverPanel
            v-slot="{ close }"
            class="z-10 flex w-screen max-w-sm flex-col gap-2 rounded-xl border bg-white p-2 shadow lg:w-64"
          >
            <button
              v-for="price in item.prices"
              :key="price.id"
              class="squircle-lg flex w-full flex-row items-center rounded border px-2 py-1 text-left"
              :class="{
                'border-primary bg-primary-50': selectedPricingId === price.id,
                'bg-white': !selectedPricingId === price.id
              }"
              @click="selectPrice(price.id, close)"
            >
              <span class="text-primary">
                <FontAwesomeIcon
                  v-if="selectedPricingId === price.id"
                  :icon="['fas', 'circle-check']"
                  class="text-primary"
                />
                <FontAwesomeIcon v-else :icon="['far', 'circle']" />
              </span>
              <div
                v-if="hidePrice"
                class="flex flex-row items-center gap-2 px-2"
              >
                <span class="font-bold">{{ price.vat_rate }}%</span>
              </div>
              <div v-else class="flex flex-row items-center gap-2 px-2">
                <span class="text-xxs uppercase">{{ displayPriceType }}</span>
                <span class="font-bold">
                  {{
                    $h.formatCurrency(
                      displayPriceType === 'HTVA'
                        ? price.price_htva
                        : price.price_tvac,
                      currency
                    )
                  }}
                </span>
                <span>({{ price.vat_rate }}%)</span>
              </div>
            </button>
          </PopoverPanel>
        </Float>
      </Popover>
    </span>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { mapGetters } from 'vuex'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { Float } from '@headlessui-float/vue'

export default defineComponent({
  name: 'ItemPricingSelector',
  components: { FontAwesomeIcon, Popover, PopoverButton, PopoverPanel, Float },
  props: {
    item: {
      type: Object,
      required: true
    },
    selectedPricingId: {
      type: String,
      required: true
    },
    hidePrice: {
      type: Boolean,
      default: false
    },
    currency: {
      type: String,
      required: true
    }
  },
  emits: ['update:item', 'update:selectedPricingId'],
  data() {
    return {
      openListingPrice: false,
      selectMultiplePrice: null
    }
  },
  computed: {
    ...mapGetters({ initialMetadata: 'metadata/getInitialMetadata' }),
    localItem: {
      get() {
        return this.item
      },
      set(value) {
        this.$emit('update:item', value)
      }
    },
    localSelectedPricingId: {
      get() {
        return this.selectedPricingId
      },
      set(value) {
        this.$emit('update:selectedPricingId', value)
      }
    },
    selectedPricing() {
      return (
        this.item?.prices?.find(
          (price) => price.id === this.selectedPricingId
        ) ?? null
      )
    },
    priceEncodingType() {
      return this.initialMetadata.settings.price_encoding_type
    },
    displayedTva() {
      if (this.item.advanced_pricing) {
        return this.selectedPricing?.vat_rate ?? '--'
      }
      return this.item.tva ?? this.item.default_tva
    },
    displayedPrice() {
      if (this.item.advanced_pricing) {
        if (this.priceEncodingType === 'HTVA') {
          return this.$h.formatCurrency(
            parseFloat(
              this.selectedPricing?.price_htva ?? this.item.default_price_htva
            ),
            this.currency
          )
        } else if (this.priceEncodingType === 'TVAC') {
          return this.$h.formatCurrency(
            parseFloat(
              this.selectedPricing?.price_tvac ?? this.item.default_price_tvac
            ),
            this.currency
          )
        }
        return null
      }
      if (this.priceEncodingType === 'HTVA') {
        return this.$h.formatCurrency(
          parseFloat(this.item.price_htva ?? this.item.default_price_htva),
          this.currency
        )
      } else if (this.priceEncodingType === 'TVAC') {
        return this.$h.formatCurrency(
          parseFloat(this.item.price_tvac ?? this.item.default_price_tvac),
          this.currency
        )
      }
      return null
    },
    displayPriceType() {
      if (this.priceEncodingType === 'HTVA') {
        return this.$t('global.excluding_vat')
      } else if (this.priceEncodingType === 'TVAC') {
        return this.$t('global.tax_included')
      }
      return null
    }
  },
  methods: {
    selectPrice(price_id, close) {
      this.localSelectedPricingId = price_id
      close()
    }
  }
})
</script>

<style scoped></style>
